/**
 * Triumph SFTP credential management — admin panel client.
 *
 * Calls `/v1/factoring-sftp/triumph/*` endpoints on main-service. The server
 * validates credentials with a live SFTP probe before persisting, so a
 * successful 200 here means the credentials WERE accepted by Triumph.
 *
 * Errors:
 *   401 — Invalid Triumph SFTP credentials
 *   503 — Triumph SFTP unreachable (IP not whitelisted, network outage)
 *   502 — SSH handshake mismatch (legacy algorithm config issue)
 */

import axios, { AxiosError } from 'axios'

const TRIUMPH_PROVIDER_SLUG = 'triumph'

interface TriumphSftpStatus {
    exists: boolean
    isValid: boolean
    host?: string
    port?: number
    username?: string
    remoteDir?: string
    lastVerifiedAt?: string
}

interface ConnectResult {
    success: boolean
    error?: string
    status?: TriumphSftpStatus
}

interface DisconnectResult {
    success: boolean
    error?: string
}

function asMessage(err: unknown): string {
    if (err instanceof AxiosError) return err.response?.data?.message || err.message
    return err instanceof Error ? err.message : 'Unexpected error'
}

export async function saveTriumphSftpCredentials(username: string, password: string): Promise<ConnectResult> {
    try {
        const response = await axios.post(`/factoring-sftp/${TRIUMPH_PROVIDER_SLUG}/connect`, { username, password })
        return { success: !!response.data?.success, status: response.data?.data }
    } catch (err) {
        return { success: false, error: asMessage(err) }
    }
}

export async function removeTriumphSftpCredentials(): Promise<DisconnectResult> {
    try {
        const response = await axios.delete(`/factoring-sftp/${TRIUMPH_PROVIDER_SLUG}/disconnect`)
        return { success: !!response.data?.success }
    } catch (err) {
        return { success: false, error: asMessage(err) }
    }
}

export async function fetchTriumphSftpStatus(): Promise<TriumphSftpStatus | null> {
    try {
        const response = await axios.get(`/factoring-sftp/${TRIUMPH_PROVIDER_SLUG}/status`)
        return response.data?.data ?? null
    } catch (err) {
        console.error('[TriumphSftp] Failed to fetch status:', asMessage(err))
        return null
    }
}
